import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

export type ThemeColor = 'teal' | 'slate' | 'emerald' | 'cyan' | 'rose' | 'orange';
export type FontSize = 'sm' | 'md' | 'lg';
export type Density = 'compact' | 'normal' | 'loose';

export interface UiState {
  // 全局加载状态
  loading: boolean;
  loadingMessage: string;
  
  // 通知系统
  notifications: Notification[];
  
  // 侧边栏状态
  sidebarOpen: boolean;
  
  // 深色模式
  darkMode: boolean;

  // 界面外观设置
  themeColor: ThemeColor;
  fontSize: FontSize;
  density: Density;
  sidebarDefaultCollapsed: boolean;
  
  // 方法
  setLoading: (loading: boolean, message?: string) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setDarkMode: (dark: boolean) => void;
  toggleDarkMode: () => void;
  setThemeColor: (color: ThemeColor) => void;
  setFontSize: (size: FontSize) => void;
  setDensity: (density: Density) => void;
  setSidebarDefaultCollapsed: (collapsed: boolean) => void;
  applyAppearance: () => void;
  resetStore: () => void;
}

const DEFAULT_STATE: Pick<UiState, 'loading' | 'loadingMessage' | 'notifications' | 'sidebarOpen' | 'darkMode' | 'themeColor' | 'fontSize' | 'density' | 'sidebarDefaultCollapsed'> = {
  loading: false,
  loadingMessage: '',
  notifications: [],
  sidebarOpen: false,
  darkMode: false,
  themeColor: 'teal',
  fontSize: 'md',
  density: 'normal',
  sidebarDefaultCollapsed: false,
};

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      setLoading: (loading: boolean, message: string = '') => {
        set({ loading, loadingMessage: message });
      },

      addNotification: (notification: Omit<Notification, 'id'>) => {
        const id = Date.now().toString() + Math.random().toString(36).slice(2, 11);
        const newNotification: Notification = {
          ...notification,
          id,
          duration: notification.duration || 3000
        };

        set((state) => ({
          notifications: [...state.notifications, newNotification]
        }));

        // 自动移除通知
        setTimeout(() => {
          get().removeNotification(id);
        }, newNotification.duration);

        return id;
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }));
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      toggleSidebar: () => {
        set((state) => ({
          sidebarOpen: !state.sidebarOpen
        }));
      },

      setDarkMode: (dark: boolean) => {
        set({ darkMode: dark });
        // 应用深色模式到文档
        if (dark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      toggleDarkMode: () => {
        set((state) => {
          const newDarkMode = !state.darkMode;
          // 应用深色模式到文档
          if (newDarkMode) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
          return { darkMode: newDarkMode };
        });
      },

      setThemeColor: (color: ThemeColor) => {
        set({ themeColor: color });
        document.documentElement.setAttribute('data-theme', color);
      },

      setFontSize: (size: FontSize) => {
        set({ fontSize: size });
        document.documentElement.setAttribute('data-font-size', size);
      },

      setDensity: (d: Density) => {
        set({ density: d });
        document.documentElement.setAttribute('data-density', d);
      },

      setSidebarDefaultCollapsed: (collapsed: boolean) => {
        set({ sidebarDefaultCollapsed: collapsed });
      },

      applyAppearance: () => {
        const state = get();
        document.documentElement.setAttribute('data-theme', state.themeColor);
        document.documentElement.setAttribute('data-font-size', state.fontSize);
        document.documentElement.setAttribute('data-density', state.density);
        if (state.darkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      resetStore: () => {
        set({ ...DEFAULT_STATE });
        document.documentElement.classList.remove('dark');
        document.documentElement.setAttribute('data-theme', 'teal');
        document.documentElement.setAttribute('data-font-size', 'md');
        document.documentElement.setAttribute('data-density', 'normal');
      }
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        darkMode: state.darkMode,
        themeColor: state.themeColor,
        fontSize: state.fontSize,
        density: state.density,
        sidebarDefaultCollapsed: state.sidebarDefaultCollapsed,
      })
    }
  )
);
